import { unstable_serialize } from 'swr'

import {
  resolvePolicy,
  type CachePolicyInput,
} from '@/core/lib/cache/cache.policy'
import {
  STANDARD_READ,
  type RequestPolicy,
} from '@/core/lib/fetcher/request-policy'

/**
 * SWR 캐시 키 계산 — 서버·클라이언트 공용.
 *
 * `'use client'`를 붙이지 않는다. 서버 컴포넌트(SWRHydrateWrapper)와
 * 클라이언트 훅(useCachedSWR)이 **반드시 같은 키**를 만들어야 하므로,
 * 키 생성 로직은 이 파일 하나만 권위를 가진다.
 * 양쪽이 각자 키를 만들면 hydration이 조용히 빗나가고, 화면은 비어 있게 된다.
 */

type PublicSwrScope = {
  readonly kind: 'public'
}

type SessionSwrScope = {
  readonly kind: 'session'
  readonly epoch: string
}

export type SwrScope = PublicSwrScope | SessionSwrScope

export type SwrKey =
  | string
  | readonly [
      string,
      {
        scope: SwrScope
        withToast?: boolean
        requestPolicy?: RequestPolicy
      },
    ]

const EPOCH_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/

export function createSessionSwrScope(epoch: string | number): SessionSwrScope {
  if (
    typeof epoch === 'number' &&
    (!Number.isSafeInteger(epoch) || epoch < 0)
  ) {
    throw new Error('Session SWR epoch number must be a non-negative integer.')
  }

  const normalized = String(epoch)
  if (!EPOCH_PATTERN.test(normalized)) {
    throw new Error('Session SWR epoch must be a stable non-secret token.')
  }

  return Object.freeze({ kind: 'session', epoch: normalized })
}

export function getSwrRequestPolicy(key: SwrKey): RequestPolicy {
  return typeof key === 'string'
    ? STANDARD_READ
    : (key[1].requestPolicy ?? STANDARD_READ)
}

function getSwrScopeIdentity(key: SwrKey): string {
  if (typeof key === 'string') return 'public'

  const scope = key[1].scope
  if (scope.kind === 'session' && !EPOCH_PATTERN.test(scope.epoch)) {
    throw new Error('Session SWR epoch must be a stable non-secret token.')
  }
  return scope.kind === 'public' ? 'public' : `session:${scope.epoch}`
}

export type SwrCacheKey = readonly [
  key: SwrKey,
  policy: Pick<ReturnType<typeof resolvePolicy>, 'preset' | 'clientCache'>,
  requestPolicyId: string,
  scopeIdentity: string,
]

/** useCachedSWR과 hydrate가 공유하는 복합 캐시 키. */
export function buildSwrCacheKey(
  key: SwrKey,
  policy: CachePolicyInput = 'REALTIME'
): SwrCacheKey {
  const resolved = resolvePolicy(policy)

  return [
    key,
    { preset: resolved.preset, clientCache: resolved.clientCache },
    getSwrRequestPolicy(key).id,
    getSwrScopeIdentity(key),
  ]
}

/**
 * SWR `fallback` 맵에 넣을 문자열 키.
 * 배열 키는 반드시 직렬화해야 매칭된다 — 평문 URL을 키로 쓰면 hydration이 실패한다.
 */
export function serializeSwrCacheKey(
  key: SwrKey,
  policy: CachePolicyInput = 'REALTIME'
): string {
  return unstable_serialize(buildSwrCacheKey(key, policy))
}
